/**
 * The questions about how someone bats and bowls (ADR-0023), as panels for the bottom half.
 *
 * Three small pieces, each a pure view of what it is handed and nothing more:
 *
 * - `HandStep` — "How does Rahul bat?" Left or right, and the tap that answers it is the commit.
 * - `BowlingStep` — "How does Ravi bowl?" An arm and a type, then Done.
 * - `ConfirmChange` — what a correction that reaches into the past says before it is applied.
 *
 * None of them writes to the ledger. The caller turns the answer into events through the same
 * `advance()` path as every other tap, so durability is not this file's business.
 */
import { useState } from "react";
import {
  BOWLING_TYPES,
  battingHandName,
  bowlingStyleName,
  bowlingTypeName,
} from "@sportsos/engine";
import type { BatterHand, BowlingArm, BowlingType } from "@sportsos/engine";
import type { BowlingChoice, KnownBowling } from "../match/styles.ts";

type Side = "left" | "right";

/** Left or right, as one segmented control: shared by the hand and the arm, which are both just a side. */
function Sides({
  label,
  value,
  onPick,
  words,
}: {
  readonly label: string;
  readonly value: Side | null;
  readonly onPick: (side: Side) => void;
  readonly words: Readonly<Record<Side, string>>;
}): React.ReactElement {
  return (
    <div className="style-sides" role="group" aria-label={label}>
      {(["left", "right"] as const).map((side) => (
        <button
          key={side}
          type="button"
          className="style-sides__item"
          aria-pressed={value === side}
          onClick={() => onPick(side)}
        >
          {words[side]}
        </button>
      ))}
    </div>
  );
}

export function HandStep({
  name,
  current,
  onPick,
  onBack,
}: {
  readonly name: string;
  /** What is known already, so a scorer changing it sees where he is starting from. */
  readonly current: BatterHand | null;
  readonly onPick: (hand: BatterHand) => void;
  readonly onBack: () => void;
}): React.ReactElement {
  return (
    <div className="stack stack--tight style-step">
      <div>
        <div className="ds-title">How does {name} bat?</div>
        <div className="ds-caption">
          {current === null ? "Asked once. It is remembered for next time." : `Saved as ${battingHandName(current)}`}
        </div>
      </div>
      <Sides
        label="Which way the batter stands"
        value={current}
        onPick={onPick}
        words={{ left: "Left-handed", right: "Right-handed" }}
      />
      <button className="ds-btn ds-btn--secondary ds-btn--block" onClick={onBack}>
        Back
      </button>
    </div>
  );
}

export function BowlingStep({
  name,
  current,
  doneLabel = "Done",
  onDone,
  onBack,
}: {
  readonly name: string;
  readonly current: KnownBowling;
  readonly doneLabel?: string;
  readonly onDone: (choice: BowlingChoice) => void;
  readonly onBack: () => void;
}): React.ReactElement {
  const [arm, setArm] = useState<BowlingArm | null>(current.arm);
  const [type, setType] = useState<BowlingType | null>(current.type);
  const style = bowlingStyleName(arm, type);

  return (
    <div className="stack stack--tight style-step">
      <div>
        <div className="ds-title">How does {name} bowl?</div>
        <div className="ds-caption">
          {arm === null && type === null ? "Asked once. The arm is remembered for good; the type can change." : (style ?? "")}
        </div>
      </div>
      <Sides label="Bowling arm" value={arm} onPick={setArm} words={{ left: "Left arm", right: "Right arm" }} />
      <div className="style-types" role="group" aria-label="Bowling type">
        {BOWLING_TYPES.map((option) => (
          <button
            key={option}
            type="button"
            className="style-types__item"
            aria-pressed={type === option}
            onClick={() => setType(option)}
          >
            {bowlingTypeName(option)}
          </button>
        ))}
      </div>
      <div className="style-step__foot">
        <button className="ds-btn ds-btn--secondary" onClick={onBack}>
          Back
        </button>
        <button
          className="ds-btn ds-btn--primary"
          disabled={arm === null || type === null}
          onClick={() => {
            if (arm !== null && type !== null) onDone({ arm, type });
          }}
        >
          {doneLabel}
        </button>
      </div>
    </div>
  );
}

/**
 * The warning before a correction that reaches back.
 *
 * Changing a batter's hand is not "from the next ball": it rewrites how every ball he has faced
 * is read, and where every shot he placed is drawn. The scorer is told that, in numbers, before
 * it happens. The safe answer is first and the default, so a tap in a hurry changes nothing.
 */
export function ConfirmChange({
  title,
  lines,
  keepLabel,
  changeLabel,
  onKeep,
  onChange,
}: {
  readonly title: string;
  readonly lines: readonly string[];
  readonly keepLabel: string;
  readonly changeLabel: string;
  readonly onKeep: () => void;
  readonly onChange: () => void;
}): React.ReactElement {
  return (
    <div className="stack stack--tight style-step" role="alertdialog" aria-label={title}>
      <div className="ds-title">{title}</div>
      <div className="style-warning">
        {lines.map((line) => (
          <p key={line}>{line}</p>
        ))}
      </div>
      <div className="style-step__foot">
        <button className="ds-btn ds-btn--secondary" onClick={onKeep}>
          {keepLabel}
        </button>
        <button className="ds-btn ds-btn--primary" onClick={onChange}>
          {changeLabel}
        </button>
      </div>
    </div>
  );
}
